import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@arcadeai/design-system/components/ui/atoms/table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  LayoutGrid,
  List,
  Plus,
  Search,
  Settings2,
} from "lucide-react";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { ResourceContainerSkeleton } from "@/components/custom/resources/resource-container-skeleton";

export const OAuthListSkeleton = () => {
  const sections = [
    { binding: "project" as const, resourceType: "providers" },
    { binding: "tenant" as const, resourceType: "providers" },
    { binding: "static" as const, resourceType: "providers" },
  ];

  return (
    <ListLayout
      actions={
        <Button disabled size="sm">
          <Plus className="mr-2 size-4" />
          Add OAuth Provider
        </Button>
      }
      description="Manage your OAuth providers for authentication."
      filters={
        <div className="flex items-center justify-between gap-1.5">
          <div className="flex items-center gap-3">
            <div className="relative max-w-md">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 w-full pl-9"
                disabled
                placeholder="Search OAuth providers..."
              />
            </div>
            <Button
              className="h-9 gap-1.5 border-dashed"
              disabled
              size="sm"
              variant="outline"
            >
              <Settings2 className="size-4" />
              Columns
            </Button>
          </div>
          <div className="flex items-center gap-1 rounded-md border p-1">
            <Button className="h-7 w-7" disabled size="icon" variant="ghost">
              <LayoutGrid className="size-4" />
            </Button>
            <Button className="h-7 w-7" disabled size="icon" variant="ghost">
              <List className="size-4" />
            </Button>
          </div>
        </div>
      }
      title="OAuth Providers"
    >
      {/* Resource Container */}
      <ResourceContainerSkeleton
        sections={sections}
        tableSkeleton={<OAuthTableSkeleton />}
      />
    </ListLayout>
  );
};

const OAuthTableSkeleton = () => {
  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <div className="flex items-center space-x-2">
                  <span>Provider</span>
                  <ChevronsUpDown className="ml-2 h-4 w-4" />
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center space-x-2">
                  <span>ID</span>
                  <ChevronsUpDown className="ml-2 h-4 w-4" />
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center space-x-2">
                  <span>Description</span>
                  <ChevronsUpDown className="ml-2 h-4 w-4" />
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center space-x-2">
                  <span>Status</span>
                  <ChevronsUpDown className="ml-2 h-4 w-4" />
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center space-x-2">Actions</div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 2 }, () => (
              <OAuthRowSkeleton key={crypto.randomUUID()} />
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm">Rows per page</p>
          <Skeleton className="h-8 w-[70px]" />
        </div>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center justify-center font-medium text-sm">
            <span className="flex items-center gap-1">
              Page 1 of <Skeleton className="inline-block h-4 w-6" />
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              className="hidden h-8 w-8 p-0 lg:flex"
              disabled
              variant="outline"
            >
              <span className="sr-only">Go to first page</span>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button className="h-8 w-8 p-0" disabled variant="outline">
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button className="h-8 w-8 p-0" disabled variant="outline">
              <span className="sr-only">Go to next page</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              className="hidden h-8 w-8 p-0 lg:flex"
              disabled
              variant="outline"
            >
              <span className="sr-only">Go to last page</span>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const OAuthRowSkeleton = () => {
  return (
    <TableRow>
      {/* Provider (icon + name + description) */}
      <TableCell>
        <div className="flex items-start gap-3 py-1">
          <Skeleton className="size-10 shrink-0 rounded-md" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-48" />
          </div>
        </div>
      </TableCell>

      {/* ID */}
      <TableCell>
        <Skeleton className="h-4 w-24" />
      </TableCell>

      {/* Description */}
      <TableCell>
        <Skeleton className="h-4 w-full max-w-sm" />
      </TableCell>

      {/* Status */}
      <TableCell>
        <Skeleton className="h-6 w-16 rounded-full" />
      </TableCell>

      {/* Actions */}
      <TableCell>
        <div className="flex items-center gap-1">
          <Button disabled size="sm" variant="outline">
            <Skeleton className="h-3 w-12" />
          </Button>
          <Button disabled size="sm" variant="outline">
            <Skeleton className="h-3 w-12" />
          </Button>
          <Separator className="mx-1 h-4" orientation="vertical" />
          <Button disabled size="sm" variant="ghost">
            <Skeleton className="h-3 w-12" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};
